import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import {
  useDriveConversationsQuery,
  DrivePromptFragment,
  DriveResponseFragment,
} from '@occc/graphql';

import type { ChatMessage } from '../types';
import { parseContentSafely } from '../utils/content-parsing-utils';
import { STALE_ERROR_CONTENT } from '../utils/error-utils';

type UseFetchConversationResult = {
  messages: ChatMessage[];
  loading: boolean;
  error: ApolloError | undefined;
};

const mapPromptToMessage = (prompt: DrivePromptFragment): ChatMessage => ({
  type: 'prompt',
  id: prompt.id,
  content: prompt.content,
  conversationId: prompt.conversationId,
  createdAt: prompt.createdAt,
});

const mapResponseToMessage = (response: DriveResponseFragment): ChatMessage => {
  const isIncomplete = !response.complete;
  const content = response.content ?? '';
  const createdAtMs = new Date(response.createdAt).getTime();
  const lastChunkNumber = response.lastChunkNumber ?? (isIncomplete ? 0 : undefined);

  return {
    type: 'response',
    id: response.id,
    content,
    conversationId: response.conversationId,
    createdAt: response.createdAt,
    promptId: response.promptId,
    parsedContent: parseContentSafely(content),
    reaction: response.reaction,
    isStreaming: isIncomplete,
    lastChunkNumber,
    // For incomplete responses loaded from the server (e.g. user navigates to a
    // conversation that is still streaming), keep them resumable even if the
    // current payload is still empty. An empty chunk is valid stream progress.
    thinkingStartedAt: isIncomplete ? createdAtMs : undefined,
  };
};

const sortByCreatedAt = (a: ChatMessage, b: ChatMessage) =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

const markNonLastIncompleteResponsesAsStale = (sorted: ChatMessage[]): ChatMessage[] => {
  // Find the last response (by position in the sorted list).
  // Any incomplete response that is NOT the last response is definitively dead
  // -- a newer prompt was sent after it failed, so it will never complete.
  let lastResponseIdx = -1;
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (sorted[i].type === 'response' || sorted[i].type === 'error') {
      lastResponseIdx = i;
      break;
    }
  }

  return sorted.map((msg, idx) => {
    if (idx !== lastResponseIdx && msg.type === 'response' && msg.isStreaming) {
      return {
        type: 'error' as const,
        id: `${msg.id}-stale`,
        content: STALE_ERROR_CONTENT,
        conversationId: msg.conversationId,
        createdAt: msg.createdAt,
        promptId: msg.promptId,
      };
    }
    return msg;
  });
};

export const useFetchConversation = (
  conversationId: string | undefined,
): UseFetchConversationResult => {
  const { data, loading, error } = useDriveConversationsQuery({
    variables: { filter: { idEquals: conversationId } },
    skip: !conversationId,
    fetchPolicy: 'cache-and-network',
  });

  const conversation = data?.driveConversations.edges[0]?.node;
  const messages = useMemo<ChatMessage[]>(
    () =>
      conversation
        ? markNonLastIncompleteResponsesAsStale(
            [
              ...conversation.prompts.map(mapPromptToMessage),
              ...conversation.responses.map(mapResponseToMessage),
            ].sort(sortByCreatedAt),
          )
        : [],
    [conversation],
  );

  return {
    messages,
    loading,
    error,
  };
};
